/* GoldenLayoutCrawler.js */

"use strict";
const _ = require("lodash");
const GoldenLayout = require("golden-layout/dist/goldenlayout.js");
const EventEmitter = require('events').EventEmitter;
const ROOT_STACK_CONFIG = {
    id: "ROOT_STACK",
    type: "stack",
    isClosable: true,
    content: []
};

class GoldenLayoutCrawler extends EventEmitter {
    constructor(rootElem, options) {
        super();
        this._rootElem = rootElem;
        this._setConfig({
            baseConfig: _.get(options, 'cfg'),
            editorItems: _.get(options, "editorItems")
        });

        this._registeredEditors = {};
        this._focusedDetails = {
            focusedStack: undefined,
            focusedItem: undefined,
        };
        this._setFocusedItem = this._setFocusedItem.bind(this);
        this._registerListeners();

        //NOTE: this is messy but it is a must for us to support onbeforeunload
        //Overwrite the Layout Mgr's close handling
        let crawlerThis = this;
        //dont remove function here we are fucking with "this"
        GoldenLayout.__lm.controls.Tab.prototype._onCloseClick = function (event) {
            let goldenLayoutThis = this;
            event.stopPropagation();
            let editorId = goldenLayoutThis.contentItem.config.id;
            let closeTheEditor = () => {
                goldenLayoutThis.header.parent.removeChild(goldenLayoutThis.contentItem);
            };
            //If someone has registered a _onBeforeUnload
            if (_.isFunction(crawlerThis.__onBeforeUnload)) {
                let resultProm = crawlerThis.__onBeforeUnload.call(crawlerThis, editorId);
                Promise.resolve(resultProm)
                    .then((result) => {
                        if (!result) {
                            closeTheEditor();
                        }
                    })
                    .catch(() => {
                        //catch is an implied cancel.
                    });
            } else {
                closeTheEditor();
            }
        };
    }

    /*******************************************/
    /* START PRIVATE METHODS */

    /*******************************************/
    _registerListeners() {
        this._rootElem.addEventListener("focusin", this._onFocusChange = this._onFocusChange.bind(this));
    }

    _unregisterListeners() {
        this._unregisterGoldenLayoutListeners();
        this._rootElem.removeEventListener("focusin", this._onFocusChange);
    }

    _registerGoldenLayoutListeners() {
        this._layoutMgr.on("itemCreated", this._onItemCreated = this._onItemCreated.bind(this));
        this._layoutMgr.on("itemDestroyed", this._onItemDestroyed = this._onItemDestroyed.bind(this));
    }

    _unregisterGoldenLayoutListeners() {
        this._layoutMgr.on("itemCreated", this._onItemCreated);
        this._layoutMgr.off("itemDestroyed", this._onItemDestroyed);
    }

    _findFirstDescendentStack(item) {
        let firstStack;
        if (item.type === 'stack') {
            firstStack = item;
        } else {
            if (item.contentItems.length) {
                item.contentItems.every((childItem) => {
                    if (childItem.type === 'stack') {
                        firstStack = childItem;
                    } else {
                        firstStack = this._findFirstDescendentStack(childItem);
                    }
                    if (firstStack) {
                        return false;
                    } else {
                        return true;
                    }
                });
            }
        }
        return firstStack;
    }

    _findClosestElement(domElem, s) {
        if (window.Element && !Element.prototype.closest) {
            let closest = (s) => {
                let matches = (this.document || this.ownerDocument).querySelectorAll(s),
                    i,
                    el = domElem;
                do {
                    i = matches.length;
                    // eslint-disable-next-line no-empty
                    while (--i >= 0 && matches.item(i) !== el) {
                    }
                } while ((i < 0) && (el = el.parentElement));
                return el;
            };
            return closest(s);
        } else {
            return domElem.closest(s);
        }
    }

    _setFocusedItem(item, noAdd) {
        if (!item) {
            this._focusedDetails.focusedItem = undefined;
            let defaultStack = this._layoutMgr.root.getItemsById(ROOT_STACK_CONFIG.id)[0];
            if (!noAdd) {
                if (!defaultStack) {
                    this._layoutMgr.root.addChild(_.cloneDeep(ROOT_STACK_CONFIG));
                    defaultStack = this._layoutMgr.root.getItemsById(ROOT_STACK_CONFIG.id)[0];
                }
            }
            this._focusedDetails.focusedStack = defaultStack;
        } else {
            this._focusedDetails.focusedItem = item;
            this._focusedDetails.focusedStack = item.parent;
        }
        //remove the class on the focused Stack and item
        let focusedStackElem = this._rootElem.querySelector(".focused_stack");
        if (focusedStackElem) {
            focusedStackElem.classList.remove('focused_stack');
        }
        let focusedItemElem = this._rootElem.querySelector(".focused_item");
        if (focusedItemElem) {
            focusedItemElem.classList.remove('focused_item');
        }
        //add a class to the focused Stack and item
        if (this._focusedDetails.focusedStack) {
            this._focusedDetails.focusedStack.element[0].classList.add("focused_stack");
        }
        if (this._focusedDetails.focusedItem) {
            this._focusedDetails.focusedItem.element[0].classList.add("focused_item");
        }
        this.emit("activeContentItemChanged", item);
    }

    _buildEditorItemsFromGoldenLayoutConfig(normalized, normalizedParentProperty, item) {
        if (!normalizedParentProperty) {
            normalizedParentProperty = normalized;
        }
        let content = _.get(item, "content");
        let normalizedParentContents = _.get(normalizedParentProperty, "content");
        if (!normalizedParentContents) {
            normalizedParentContents = normalizedParentProperty.content = [];
        }

        if (content) {
            content.forEach((child) => {
                let type = _.get(child, "type");
                let normalizedChild;
                switch (type) {
                    case 'component':
                        normalizedParentContents.push({
                            id: child.id,
                            type: child.type,
                            editorType: child.componentName,
                            editorState: child.componentState
                        });
                        break;
                    //NOTE: These (Stack,Row,Column) should fall through. (Jacob Locken)
                    case 'stack':
                    case 'row':
                    case 'column':
                        normalizedChild = {
                            type: child.type,
                            width: child.width,
                            height: child.height,
                            content: []
                        };
                        //stack specific details
                        if (type === 'stack') {
                            normalizedChild.activeEditorId = child.content[child.activeItemIndex].id;
                            if (_.isArray(child.id) && child.id.length === 2) {
                                normalizedChild.maximizedInfo = {
                                    containerId: child.id[0],
                                    tagName: child.id[1]
                                };
                                normalized.maximisedEditorId = normalizedChild.activeEditorId;
                            }
                        }
                        normalizedParentContents.push(normalizedChild);
                        this._buildEditorItemsFromGoldenLayoutConfig(normalized, normalizedChild, child);
                        break;
                }
            });
        }
    }

    _getEditorItemsFromGoldenLayoutConfig() {
        let config = this._layoutMgr.toConfig();
        let normalizedCfg = {};
        this._buildEditorItemsFromGoldenLayoutConfig(normalizedCfg, null, config);
        return normalizedCfg;
    }

    _buildGoldenLayoutConfigFromEditorItems(unNormalized, unNormalizedParentProperty, item) {
        if (!unNormalizedParentProperty) {
            unNormalizedParentProperty = unNormalized;
        }
        let content = _.get(item, "content");
        let normalizedParentContents = _.get(unNormalizedParentProperty, "content");
        if (!normalizedParentContents) {
            normalizedParentContents = unNormalizedParentProperty.content = [];
        }

        if (content) {
            content.forEach((child) => {
                let type = _.get(child, "type");
                let normalizedChild;
                switch (type) {
                    case 'component':
                        normalizedParentContents.push({
                            id: child.id,
                            type: child.type,
                            componentName: child.editorType,
                            componentState: child.editorState
                        });
                        break;
                    //NOTE: These (Stack,Row,Column) should fall through. (Jacob Locken)
                    case 'stack':
                    case 'row':
                    case 'column':
                        normalizedChild = {
                            type: child.type,
                            width: child.width,
                            height: child.height,
                            content: []
                        };
                        //stack specific details
                        if (type === 'stack') {
                            normalizedChild.activeItemIndex = _.findIndex(child.content, {id: child.activeEditorId});
                        }
                        if (child.maximizedInfo) {
                            normalizedChild.id = [child.maximizedInfo.containerId, child.maximizedInfo.tagName];
                            unNormalized.maximisedItemId = child.maximizedInfo.tagName;
                        }
                        normalizedParentContents.push(normalizedChild);
                        this._buildGoldenLayoutConfigFromEditorItems(unNormalized, normalizedChild, child);
                        break;
                }
            });
        }
    }

    _getGoldenLayoutConfigFromEditorItems(editorItems) {
        let glCfg = {};
        this._buildGoldenLayoutConfigFromEditorItems(glCfg, null, editorItems);
        return glCfg;
    }

    _setConfig(options) {
        this._config = _.get(options, 'baseConfig', {
            settings: {
                showPopoutIcon: false,
                showCloseIcon: true
            },
            labels: {
                close: 'Close',
                maximise: 'Maximize',
                minimise: 'Minimize',
                tabDropdown: 'Additional Tabs'
            },
            dimensions: {
                headerHeight: 45,
                borderWidth: 10
            },
            content: []
        });

        if (_.get(options, "editorItems")) {
            let generatedConfig = this._getGoldenLayoutConfigFromEditorItems(options.editorItems);
            _.merge(this._config, generatedConfig);
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START HANDLER METHODS */

    /*******************************************/
    _onFocusChange(evt) {
        let editorId;
        if (evt.target.dataset.editorId) {
            editorId = evt.target.dataset.editorId;
        } else {
            let itemElement = this._findClosestElement(evt.target, ".lm_item_container");
            editorId = itemElement.dataset.editorId;
        }
        if (editorId !== _.get(this._focusedDetails, 'focusedItem.config.id')) {
            let item = this._layoutMgr.root.getItemsByFilter((itm) => {
                let config = itm.config;
                let itmId;
                switch (itm.type) {
                    case "component":
                        itmId = config.id;
                        if (itmId === editorId) {
                            return true;
                        }
                        break;
                }
                return false;
            });
            this._setFocusedItem(item[0]);
            this.emit("EditorFocused", editorId);
        }
    }

    _onItemDestroyed(item) {
        let config = item.config;
        let editorId, parent;
        switch (item.type) {
            case "component":
                //cleanup local references to components
                editorId = config.id;
                this.emit("EditorDestroyed", editorId);
                break;
            case "stack":
                parent = item.parent;
                item.off('activeContentItemChanged', this._setFocusedItem);
                if (parent.contentItems.length) {
                    let nextInLineStack;
                    let nextInLineItem;

                    if (parent.isRoot) {
                        //give Golden Layout a chance to clean things up before recreating the ROOT_STACK
                        setTimeout(() => {
                            this._setFocusedItem(undefined, true);
                        });
                    } else {
                        let destroyedIdx = _.findIndex(parent.contentItems, (o) => {
                            let itemId = _.get(o, "config.id");
                            return itemId === config.id;
                        });
                        if (parent.contentItems.length > 1) {
                            if (destroyedIdx === 0) {
                                nextInLineStack = this._findFirstDescendentStack(parent.contentItems[1]);
                            } else {
                                nextInLineStack = this._findFirstDescendentStack(parent.contentItems[destroyedIdx - 1]);
                            }
                            nextInLineItem = nextInLineStack.getActiveContentItem();
                        }
                        this._setFocusedItem(nextInLineItem);
                    }
                }
                break;
        }
    }

    _onItemCreated(item) {
        let config = item.config;
        let editorId;
        switch (item.type) {
            case "component":
                editorId = config.id;
                //make the item focusable
                item.element[0].setAttribute('tabindex', '0');
                item.element[0].dataset.editorId = editorId;
                this.emit("EditorCreated", editorId);
                this._setFocusedItem(item);
                break;
            case "stack":
                if (!item.config.id) {
                    item.config.id = Date.now().toString();
                }
                item.on('activeContentItemChanged', this._setFocusedItem);
                break;
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PUBLIC METHODS */

    /*******************************************/
    shutdown() {
        let prom = new Promise((resolve/*,reject*/) => {
            this._unregisterListeners();
            this._rootElem = undefined;
            this._layoutMgr.destroy();
            this._layoutMgr = undefined;
            this._registeredEditors = undefined;
            this._focusedDetails = undefined;
            this._config = undefined;
            resolve();
            this.emit("ShutdownCompleted");
        });
        return prom;
    }

    initialize() {
        return new Promise((resolve, reject) => {//jshint ignore:line
            let hdlr = () => {
                if (this._layoutMgr) {
                    resolve();
                    this.emit("Initialized");
                    this._layoutMgr.off("initialised", hdlr);
                } else {
                    reject({
                        errorCode: "SHUTDOWN_ENCOUNTERED",
                        errorMsg: "A shutdown was received while we were starting up the GoldenLayout widget."
                    });
                }
            };
            if (this._layoutMgr) {
                this._unregisterGoldenLayoutListeners();
                this._layoutMgr.destroy();
            }
            this._layoutMgr = new GoldenLayout(this._config, this._rootElem);
            this._layoutMgr.focusEditor = this.focusEditor = this.focusEditor.bind(this);
            //reregister known Editors
            Object.keys(this._registeredEditors).forEach((editorTypeName) => {
                this.registerEditor(editorTypeName, this._registeredEditors[editorTypeName]);
            });
            //listeners
            this._registerGoldenLayoutListeners();
            this._layoutMgr.on("initialised", hdlr);
            //tell Golden Layout to initialize
            this._layoutMgr.init();
        });
    }

    registerOnBeforeUnload(onBeforeUnloadFn) {
        if (_.isFunction(onBeforeUnloadFn)) {
            this.__onBeforeUnload = onBeforeUnloadFn;
        }
    }

    registerEditor(name, editorFactory) {
        this._registeredEditors[name] = editorFactory;
        this._layoutMgr.registerComponent(name, editorFactory);
    }

    focusEditor(editorId) {
        let editorToFocus = this._layoutMgr.root.getItemsById(editorId)[0];
        if (editorToFocus) {
            let stack = editorToFocus.parent;
            if (_.get(stack, "type") === 'stack') {
                let activeItem = stack.getActiveContentItem();
                if (activeItem !== editorToFocus) {
                    stack.setActiveContentItem(editorToFocus);
                }
            }
            this._setFocusedItem(editorToFocus, true);
        }
    }

    closeEditor(editorId) {
        let editorToClose = this._layoutMgr.root.getItemsById(editorId)[0];
        if (editorToClose) {
            editorToClose.remove();
        }
    }

    launchEditor(goldenLayoutComponentName, componentState) {
        // eslint-disable-next-line no-unused-vars
        return new Promise((resolve, reject) => {
            let editorName = componentState.editorName;
            let editorState = componentState.editorState;
            let launchOptions = componentState.launchOptions;

            let editorId = launchOptions.id;
            if (!editorId) {
                editorId = Date.now().toString();
            }
            componentState.id = editorId;

            let componentItem = this._layoutMgr.root.getItemsById(editorId)[0];

            //Add editor to container
            if (!componentItem) {
                if (!this._focusedDetails.focusedStack) {
                    this._setFocusedItem(null);
                }
                this._focusedDetails.focusedStack.addChild({
                    id: componentState.id,
                    type: "component",
                    componentName: goldenLayoutComponentName,
                    componentState: componentState
                });
                componentItem = this._focusedDetails.focusedStack.getItemsById(editorId)[0];
            }
            //Make this component the active component
            else {
                this.focusEditor(editorId);
                // update the component state
                if (_.isFunction(_.get(componentItem, "instance.setEditorState"))) {
                    componentItem.instance.setEditorState(editorState);
                }
            }

            let results = {
                editorId: editorId,
                editorComponent: componentItem,
                editorState: editorState,
                editorName: editorName,
                launchOptions: launchOptions
            };
            resolve(results);
            this.emit("EditorLaunched", results);
        });
    }

    toggleFullscreen(editorId) {
        if (editorId) {
            let componentItem = this._layoutMgr.root.getItemsById(editorId)[0];
            if (_.get(componentItem, "type") === 'component') {
                componentItem.parent.toggleMaximise();
            }
        } else if (this._focusedDetails.focusedItem) {
            this._focusedDetails.focusedStack.toggleMaximise();
        }
    }

    updateSize(width, height) {
        this._layoutMgr.updateSize(width, height);
    }

    get editorItems() {
        return this.getEditorItems();
    }

    getEditorItems() {
        return this._getEditorItemsFromGoldenLayoutConfig();
    }

    reinitialize(editorItems) {
        this._setConfig({
            editorItems: editorItems
        });
        this.initialize();
    }
}

module.exports = GoldenLayoutCrawler;
